import React, { useState } from "react";
import BookingCard from "./BookingCard";
import { Booking } from "../types/booking";
import AppointmentSidebar from "./AppointmentSidebar";
import { getBookingEdit } from "../services/bookings";
import { getBusinessServices } from "../services/businessServicesApi";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import * as Popover from "@radix-ui/react-popover";
import { useToast } from "../hooks/useToast";
import GlobalLoader from "./ui/GlobalLoader";

interface CalendarGridProps {
  bookings: Booking[];
  selectedStaff: string | null;
  view: "day" | "week";
  selecteddate: Date;
  staffList?: { id: number; name: string; color: string }[];
  onRefresh?: () => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  bookings,
  selectedStaff,
  view,
  selecteddate,
  staffList: propStaffList,
  onRefresh,
}) => {
  const toast = useToast();
  const [selectedSlot, setSelectedSlot] = useState<{
    time: string;
    staff: string;
    date: string;
  } | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);

  const [rebookMode, setRebookMode] = useState(false);
  const [filteredServices, setFilteredServices] = useState<any[]>([]);
  const [activeWeekSlot, setActiveWeekSlot] = React.useState<{
    hour: number;
    minute: number;
    dayIdx: number;
  } | null>(null);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const VISIBLE_COUNT = 5;


  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  // Use props for staff list, fallback to empty array if undefined
  const staff = propStaffList || [];
  const staffList = staff;

  const displayedStaff = selectedStaff
    ? staff.filter((s) => s.name === selectedStaff)
    : staff;

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    if (startIndex > 0 && startIndex + VISIBLE_COUNT > displayedStaff.length) {
      setStartIndex(Math.max(0, displayedStaff.length - VISIBLE_COUNT));
    }
  }, [displayedStaff.length, startIndex]);

  const timeSlots = [
    "9 AM",
    "10 AM",
    "11 AM",
    "12 PM",
    "1 PM",
    "2 PM",
    "3 PM",
    "4 PM",
    "5 PM",
    "6 PM",
    "7 PM",
    "8 PM",
    "9 PM",
    "10 PM",
    "11 PM",
  ];

  const rowHeight = 100;

  const MIN_BOOKING_HEIGHT = 40;

  const getBookingPosition = (booking: Booking) => {
    const startDate = new Date(booking.start);
    const endDate = new Date(booking.end);

    const startHour = startDate.getHours();
    const startMinute = startDate.getMinutes();
    const endHour = endDate.getHours();
    const endMinute = endDate.getMinutes();

    const startFromNine = (startHour - 9) * 60 + startMinute;
    const endFromNine = (endHour - 9) * 60 + endMinute;

    let duration = endFromNine - startFromNine;
    if (duration <= 0) duration = 15;

    const top = (startFromNine * rowHeight) / 60;
    let height = (duration * rowHeight) / 60;

    if (height < MIN_BOOKING_HEIGHT) height = MIN_BOOKING_HEIGHT;

    return { top: `${top}px`, height: `${height}px` };
  };

  const formatTime = (hour: number, minute: number) =>
    new Date(0, 0, 0, hour, minute).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const getDateForWeekDay = (selecteddate: Date, dayIdx: number) => {
    const startOfWeek = new Date(selecteddate);
    const day = startOfWeek.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    startOfWeek.setDate(startOfWeek.getDate() + diff + dayIdx);
    return startOfWeek.toLocaleDateString("en-CA");
  };

  const handleSlotClick = (
    hour: number,
    minute: number,
    staffName: string,
    forcedDate?: string
  ) => {
    const time = formatTime(hour, minute);
    const date = forcedDate || selecteddate.toLocaleDateString("en-CA");

    setSelectedSlot({ time, staff: staffName, date });
    setIsSidebarOpen(true);
    setRebookMode(false);

    setFilteredServices([]);
    setServicesLoading(true);

    const stylist = staffList.find((s) => s.name === staffName);
    const stylist_id = stylist?.id || 1;

    getBusinessServices()
      .then((allServices) => {
        const mappedServices = allServices.map(s => ({
          id: s.id,
          name: s.name,
          duration: s.durationLabel,
          price: String(s.price),
          company_id: Number((s.raw as any)?.company_id || 0),
          staffIds: s.staff.map(m => Number(m.id)),
          categoryId: (s.raw as any)?.category_id,
          locationId: (s.raw as any)?.location_id,
          description: (s.raw as any)?.description,
          discount: (s.raw as any)?.discount,
          discount_type: (s.raw as any)?.discount_type,
          service_type: (s.raw as any)?.service_type,
          tax_ids: (s.raw as any)?.service_taxes?.map((t: any) => t.tax_id || t.id) || []
        }));
        setFilteredServices(mappedServices);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load services for this slot");
      })
      .finally(() => setServicesLoading(false));
  };

  const handleBookingClick = async (bookingId: number) => {
    setBookingLoading(true);
    try {
      const bookingData = await getBookingEdit(bookingId);
      console.log("Booking data in the FE", bookingData);

      const booking = bookingData.booking;

      const mappedServices = booking.items.map((item: any) => ({
        id: "service-" + (item.business_service_id || item.business_service?.id || item.id),
        name: item.business_service?.name || item.name || "Service",
        duration: item.business_service
          ? `${item.business_service.time} ${item.business_service.time_type}`
          : "60 min",
        time: item.business_service?.time || 60,
        time_type: item.business_service?.time_type || "minutes",
        price: item.business_service?.price || item.unit_price || "0",
        staffIds: item.business_service?.users?.map((u: any) => Number(u.id)) || [],
        categoryId: item.business_service?.category_id || null,
        locationId: item.business_service?.location_id || null,
        description: item.business_service?.description || "",
        discount: item.business_service?.discount || 0,
        discount_type: item.business_service?.discount_type || "percent",
        service_type: item.business_service?.service_type || "offline",
        tax_ids: item.business_service?.service_taxes
          ? item.business_service.service_taxes.map((t: any) => Number(t.tax_id || t.id))
          : [],
        categoryColor: item.business_service?.category?.color_code || null,
      }));

      const categoryColor = mappedServices[0]?.categoryColor || null;

      const mappedBooking = {
        id: bookingId,
        customer: booking.user ? {
          id: booking.user.id,
          name: booking.user.name,
          email: booking.user.email,
          phone: booking.user.phone || booking.user.formatted_mobile,
          user_image_url: booking.user.user_image_url
        } : { id: 0, name: "Unknown Client", email: "" },
        services: mappedServices,
        service: mappedServices[0] || null,
        note: booking.additional_notes || "",
        status: booking.status,
        date: booking.date || booking.booking_date || booking.start?.split(" ")[0] || booking.date_time?.split(" ")[0] || new Date().toISOString().split("T")[0],
        type: booking.booking_type,
        categoryColor: categoryColor,
        staffName: booking.users?.[0]?.name || "",
        rawBooking: booking,
      };

      console.log("---------------------", mappedBooking);

      const existingBooking = bookings.find((item) => item.id === bookingId);
      // Prioritize existingBooking.start since it's already correctly mapped in the parent
      const startStr = existingBooking?.start || bookingData.booking.date_time || bookingData.booking.start;

      let validStartDate: Date;
      if (startStr) {
        // Handle potential formats (ISO, space-separated, etc.)
        const isoStr = startStr.includes(" ") ? startStr.replace(" ", "T") : startStr;
        const parsedDate = new Date(isoStr);
        validStartDate = isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
      } else {
        validStartDate = new Date();
      }

      const dateStr = startStr ? startStr.split(/[ T]/)[0] : new Date().toLocaleDateString("en-CA");

      setSelectedSlot({
        time: validStartDate.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        staff: bookingData.booking.staffName || existingBooking?.staff || "",
        date: dateStr,
      });

      setSelectedBooking(mappedBooking);
      setIsSidebarOpen(true);
      setRebookMode(false);
    } catch (err) {
      console.error("Error fetching booking edit:", err);
      toast.error("Failed to retrieve booking details");
    } finally {
      setBookingLoading(false);
    }
  };

  const getBookingsForWeekDay = (
    bookings: Booking[],
    staffName: string,
    dayIdx: number
  ) => {
    return bookings.filter((b) => {
      if (!b.staff || b.staff.trim() === "") return false;
      if (b.staff !== staffName) return false;

      const bookingDate = new Date(b.start);

      // CALCULATE WEEK START (MONDAY)
      const bookingDayOfWeek = bookingDate.getDay(); // 0=Sunday
      const mondayOffset = bookingDayOfWeek === 0 ? -6 : 1 - bookingDayOfWeek;
      const weekStart = new Date(bookingDate);
      weekStart.setHours(0, 0, 0, 0);
      weekStart.setDate(bookingDate.getDate() + mondayOffset);

      //DIFFERENCE IN THE BOOKING DATE AND WEEK START
      const diffDays = Math.floor(
        (bookingDate.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24)
      );

      return diffDays === dayIdx;
    });
  };

  const isDesktop = windowWidth >= 1024;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const chunkSize = isTablet ? 2 : 1; // Used only for mobile/tablet looping

  const visibleStaff = isDesktop
    ? displayedStaff.slice(startIndex, startIndex + VISIBLE_COUNT)
    : displayedStaff;

  const canNextStaff = isDesktop && startIndex + VISIBLE_COUNT < displayedStaff.length;
  const canPrevStaff = isDesktop && startIndex > 0;

  const actualColCount = isDesktop
    ? (view === "week" ? 7 : (displayedStaff.length > 0 ? Math.min(displayedStaff.length, VISIBLE_COUNT) : 1))
    : 7; // Only relevant for desktop layout logic when manually applying grid

  const gridColumns = `80px repeat(${actualColCount}, minmax(140px, 1fr))`;

  const weekDays = [
    "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
  ];

  // Helper to chunk array
  const chunkArray = <T,>(arr: T[], size: number): T[][] => {
    const res: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      res.push(arr.slice(i, i + size));
    }
    return res;
  };

  const staffChunks = view === 'day' ? chunkArray(displayedStaff, chunkSize) : [];
  const dayChunks = view === 'week' ? chunkArray(weekDays, chunkSize) : [];
  const chunks = view === 'day' ? staffChunks : dayChunks;

  const [expandedChunkIndices, setExpandedChunkIndices] = useState<number[]>([]);

  const toggleChunkHelper = (index: number) => {
    setExpandedChunkIndices(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  return (
    <div className="flex flex-col w-full relative pb-10 md:mt-4 lg:mt-0">

      {/* DESKTOP VIEW MOCKUP (Restored Pagination) */}
      {isDesktop && (
        <div className="flex flex-col bg-white border border-gray-200 rounded-xl overflow-hidden max-w-full">
          {/* Header Row */}
          <div className="grid flex-1" style={{ gridTemplateColumns: gridColumns }}>
            <div className="h-12 border-b border-r border-gray-200 bg-white flex items-center justify-center gap-1">
              {/* Pagination Controls */}
              {displayedStaff.length > VISIBLE_COUNT && view === "day" && (
                <>
                  <button
                    onClick={() => setStartIndex(Math.max(0, startIndex - 1))}
                    disabled={!canPrevStaff}
                    className="p-1 hover:bg-gray-50 disabled:opacity-20 disabled:cursor-not-allowed text-[#885ABB] rounded-md transition-colors"
                    title="Scroll Left"
                  >
                    <ChevronLeft size={20} strokeWidth={3} />
                  </button>
                  <button
                    onClick={() => setStartIndex(Math.min(displayedStaff.length - VISIBLE_COUNT, startIndex + 1))}
                    disabled={!canNextStaff}
                    className="p-1 hover:bg-gray-50 disabled:opacity-20 disabled:cursor-not-allowed text-[#885ABB] rounded-md transition-colors"
                    title="Scroll Right"
                  >
                    <ChevronRight size={20} strokeWidth={3} />
                  </button>
                </>
              )}
            </div>
            {view === "day" ? (
              visibleStaff.length > 0 ? (
                visibleStaff.map((s, index) => (
                  <div
                    key={`${s.name}-${index}`}
                    style={{ backgroundColor: s.color }}
                    className="text-white text-center py-3 font-medium border-r border-gray-200 last:border-r-0 truncate px-2"
                  >
                    {s.name}
                  </div>
                ))
              ) : (
                <div className="bg-gray-50 border-b border-gray-200 flex items-center justify-center text-gray-500 font-medium">
                  No Staff found
                </div>
              )
            ) : (
              weekDays.map((day) => (
                <div
                  key={day}
                  style={{ backgroundColor: "#885ABB" }}
                  className="text-white text-center py-3 font-medium border-r border-gray-200 last:border-r-0"
                >
                  {day}
                </div>
              ))
            )}
          </div>

          {/* Body Row */}
          <div className="grid flex-1" style={{ gridTemplateColumns: gridColumns }}>

            {/* Time Column */}
            <div className="border-r border-gray-200">
              <div className="border-b border-gray-200"></div>
              {timeSlots.map((time) => (
                <div key={time} className="h-[100px] border-b border-gray-200 flex items-start justify-end pr-3 pt-1">
                  <span className="text-sm text-gray-500">{time}</span>
                </div>
              ))}
            </div>

            {/* Staff / Days Columns */}
            {view === "day" && visibleStaff.map((staffMember, index) => (
              <div key={staffMember.name} className="relative border-r border-l border-gray-200 last:border-r-0 overflow-visible">
                <div className="border-b border-gray-200"></div>
                {timeSlots.map((_, hourIdx) => {
                  const baseHour = 9 + hourIdx;
                  return (
                    <React.Fragment key={hourIdx}>
                      {[0, 15, 30, 45].map((m) => (
                        <div
                          key={m}
                          className={`relative group hover:bg-purple-50 transition cursor-pointer flex items-center justify-center h-[25px] ${m === 0 || m === 30 ? "border-t border-gray-200" : ""}`}
                          onClick={() => handleSlotClick(baseHour, m, staffMember.name)}
                        >
                        </div>
                      ))}
                    </React.Fragment>
                  );
                })}

                {/* Bookings */}
                {bookings
                  .filter(b => b.staff === staffMember.name)
                  .map(b => {
                    const pos = getBookingPosition(b);
                    return (
                      <BookingCard
                        key={b.id}
                        booking={b}
                        bgColor={staffMember.color}
                        style={{ top: pos.top, height: pos.height, width: "100%" }}
                        onClick={() => handleBookingClick(b.id)}
                        isLastColumn={
                          index === visibleStaff.length - 1 ||
                          (visibleStaff.length >= 4 && index === visibleStaff.length - 2)
                        }
                        showAbove={visibleStaff.length === 1}
                      />
                    );
                  })
                }
              </div>
            ))}

            {view === "week" && weekDays.map((day, dayIdx) => (
              <div key={day} className="relative border-r border-l border-gray-200 last:border-r-0 overflow-visible">
                <div className="border-b border-gray-200"></div>
                {timeSlots.map((_, hourIdx) => {
                  const baseHour = 9 + hourIdx;
                  return (
                    <React.Fragment key={hourIdx}>
                      {[0, 15, 30, 45].map((m) => (
                        <div
                          key={m}
                          className={`relative group hover:bg-purple-50 transition cursor-pointer flex items-center justify-center h-[25px] ${m === 0 || m === 30 ? "border-t border-gray-200" : ""}`}
                        >
                          <Popover.Root
                            open={activeWeekSlot?.hour === baseHour && activeWeekSlot?.minute === m && activeWeekSlot?.dayIdx === dayIdx}
                            onOpenChange={(open) => !open && setActiveWeekSlot(null)}
                          >
                            <Popover.Trigger className="absolute inset-0 w-full h-full" onClick={() => setActiveWeekSlot({ hour: baseHour, minute: m, dayIdx })} />
                            <Popover.Content side="right" align="start" className="w-48 bg-white rounded-lg shadow-lg border border-gray-300 z-50">
                              <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-700">Select Staff Member</div>
                              <div className="flex flex-col p-2 gap-1 max-h-48 overflow-y-auto">
                                {displayedStaff.map(s => (
                                  <button key={s.name || s.id} className="flex items-center px-3 py-2 rounded hover:bg-purple-50 text-sm text-gray-800 font-medium transition"
                                    onClick={() => {
                                      const computedDate = getDateForWeekDay(selecteddate, dayIdx);
                                      handleSlotClick(baseHour, m, s.name, computedDate);
                                      setActiveWeekSlot(null);
                                    }}
                                  >
                                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: s.color }} />
                                    {s.name}
                                  </button>
                                ))}
                              </div>
                              <Popover.Arrow className="fill-white" />
                            </Popover.Content>
                          </Popover.Root>
                        </div>
                      ))}
                    </React.Fragment>
                  );
                })}

                {/* Week View Bookings */}
                {displayedStaff.map(staffMember => {
                  const bookingsForThisDay = getBookingsForWeekDay(bookings, staffMember.name, dayIdx);
                  return bookingsForThisDay.map(b => {
                    const pos = getBookingPosition(b);
                    return (
                      <BookingCard
                        key={b.id}
                        booking={b}
                        bgColor={staffMember.color}
                        style={{ top: pos.top, height: pos.height, position: "absolute", width: "100%" }}
                        onClick={() => handleBookingClick(b.id)}
                        isLastColumn={dayIdx >= 5}
                      />
                    );
                  });
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MOBILE / TABLET VIEW (Responsive Chunking) */}
      {!isDesktop && (
        <div className="flex flex-col gap-8 w-full">
          {(!displayedStaff || displayedStaff.length === 0) && view === 'day' && (
            <div className="bg-white p-8 text-center border border-gray-200 rounded-lg">
              <p className="text-gray-500">No staff members assigned</p>
            </div>
          )}
          {chunks.map((chunkItems: any[], chunkIndex) => {
            const chunkGridTemplate = `80px repeat(${chunkItems.length}, minmax(140px, 1fr))`;
            const isChunkExpanded = expandedChunkIndices.includes(chunkIndex); // Removed isDesktop check here
            const collapsedHeightClass = isTablet ? 'max-h-[350px]' : 'max-h-[150px]';

            return (
              <div key={chunkIndex} className="bg-white border border-gray-200 rounded-xl overflow-hidden w-full relative shadow-none lg:shadow-sm">

                {/* SCROLLABLE / CLIPPED CONTENT AREA */}
                <div
                  className={`w-full transition-all duration-500 ease-in-out ${isChunkExpanded ? '' : `${collapsedHeightClass} overflow-hidden`}`}
                >
                  {/* HEADER OF CHUNK */}
                  <div className="grid w-full overflow-x-auto" style={{ gridTemplateColumns: chunkGridTemplate }}>
                    <div className="h-12 bg-white z-20 border-r border-gray-200 border-b"></div>
                    {view === 'day'
                      ? (chunkItems as typeof displayedStaff).map((s) => (
                        <div key={s.name || s.id} style={{ backgroundColor: s.color }} className="text-white text-center py-3 font-medium border-r border-b border-gray-200 last:border-r-0">
                          {s.name}
                        </div>
                      ))
                      : (chunkItems as string[]).map(day => (
                        <div key={day} style={{ backgroundColor: "#885ABB" }} className="text-white text-center py-3 font-medium border-r border-b border-gray-200 last:border-r-0">
                          {day}
                        </div>
                      ))
                    }
                  </div>

                  {/* BODY OF CHUNK */}
                  <div className="grid w-full" style={{ gridTemplateColumns: chunkGridTemplate }}>
                    {/* TIME COLUMN */}
                    <div className="bg-white z-20 border-r border-gray-200">
                      {timeSlots.map((time) => (
                        <div key={time} className="h-[100px] border-b border-gray-200 flex items-start justify-end pr-3 pt-1">
                          <span className="text-sm text-gray-500">{time}</span>
                        </div>
                      ))}
                    </div>

                    {/* COLUMNS */}
                    {view === 'day'
                      ? (chunkItems as typeof displayedStaff).map((staffMember, indexInChunk) => (
                        <div key={staffMember.name || staffMember.id} className="relative border-r border-l border-gray-200 last:border-r-0">
                          {/* Time slots interactions */}
                          {timeSlots.map((_, hourIdx) => {
                            const baseHour = 9 + hourIdx;
                            return (
                              <React.Fragment key={hourIdx}>
                                {[0, 15, 30, 45].map((m) => (
                                  <div key={m} className={`relative group hover:bg-purple-50 transition cursor-pointer flex items-center justify-center h-[25px] ${m === 0 || m === 30 ? "border-t border-gray-200" : ""}`}
                                    onClick={() => handleSlotClick(baseHour, m, staffMember.name)}
                                  >
                                  </div>
                                ))}
                              </React.Fragment>
                            );
                          })}

                          {/* Bookings */}
                          {bookings
                            .filter((b) => b.staff === staffMember.name)
                            .map((b) => {
                              const pos = getBookingPosition(b);
                              return (
                                <BookingCard
                                  key={b.id}
                                  booking={b}
                                  bgColor={staffMember.color}
                                  style={{ top: pos.top, height: pos.height, position: "absolute", width: "100%" }}
                                  onClick={() => handleBookingClick(b.id)}
                                  isLastColumn={indexInChunk === chunkItems.length - 1}
                                  showAbove={chunkItems.length === 1}
                                />
                              );
                            })}
                        </div>
                      ))
                      : (chunkItems as string[]).map((day, indexInChunk) => {
                        const originalDayIdx = weekDays.indexOf(day);
                        return (
                          <div key={day} className="relative border-r border-l border-gray-200 last:border-r-0">
                            {timeSlots.map((_, hourIdx) => {
                              const baseHour = 9 + hourIdx;
                              return (
                                <React.Fragment key={hourIdx}>
                                  {[0, 15, 30, 45].map((m) => (
                                    <div key={m} className={`relative group hover:bg-purple-50 transition cursor-pointer flex items-center justify-center h-[25px] ${m === 0 || m === 30 ? "border-t border-gray-200" : ""}`}>
                                      <Popover.Root
                                        open={activeWeekSlot?.hour === baseHour && activeWeekSlot?.minute === m && activeWeekSlot?.dayIdx === originalDayIdx}
                                        onOpenChange={(open) => !open && setActiveWeekSlot(null)}
                                      >
                                        <Popover.Trigger className="absolute inset-0 w-full h-full" onClick={() => setActiveWeekSlot({ hour: baseHour, minute: m, dayIdx: originalDayIdx })} />
                                        <Popover.Content side="right" align="start" className="w-48 bg-white rounded-lg shadow-lg border border-gray-300 z-50">
                                          <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-700">Select Staff Member</div>
                                          <div className="flex flex-col p-2 gap-1 max-h-48 overflow-y-auto">
                                            {displayedStaff.map((s) => (
                                              <button key={s.name || s.id} className="flex items-center px-3 py-2 rounded hover:bg-purple-50 text-sm text-gray-800 font-medium transition"
                                                onClick={() => {
                                                  const computedDate = getDateForWeekDay(selecteddate, originalDayIdx);
                                                  handleSlotClick(baseHour, m, s.name, computedDate);
                                                  setActiveWeekSlot(null);
                                                }}
                                              >
                                                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: s.color }} />
                                                {s.name}
                                              </button>
                                            ))}
                                          </div>
                                          <Popover.Arrow className="fill-white" />
                                        </Popover.Content>
                                      </Popover.Root>
                                    </div>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                            {displayedStaff.map((staffMember) => {
                              const bookingsForThisDay = getBookingsForWeekDay(bookings, staffMember.name, originalDayIdx);
                              return bookingsForThisDay.map((b) => {
                                const pos = getBookingPosition(b);
                                return (
                                  <BookingCard
                                    key={b.id}
                                    booking={b}
                                    bgColor={staffMember.color}
                                    style={{ top: pos.top, height: pos.height, position: "absolute", width: "100%" }}
                                    onClick={() => handleBookingClick(b.id)}
                                    isLastColumn={indexInChunk === chunkItems.length - 1}
                                    showAbove={chunkItems.length === 1}
                                  />
                                );
                              });
                            })}
                          </div>
                        );
                      })
                    }
                  </div>
                </div>

                {/* FADE OVERLAY & TOGGLE BUTTON (Mobile/Tablet Only) */}
                {!isChunkExpanded && (
                  <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white via-white/80 to-transparent z-30 pointer-events-none" />
                )}

                <button
                  onClick={() => toggleChunkHelper(chunkIndex)}
                  className={`absolute bottom-0 left-0 w-full flex items-center justify-center py-2 bg-white/95 backdrop-blur-sm border-t border-gray-100 hover:bg-gray-50 transition-all text-[#885ABB] z-40 ${isChunkExpanded ? 'relative border-t-2' : ''}`}
                >
                  {isChunkExpanded ? (
                    <ChevronUp className="w-6 h-6 md:w-8 md:h-8" strokeWidth={3} />
                  ) : (
                    <ChevronDown className="w-6 h-6 md:w-8 md:h-8" strokeWidth={3} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {rebookMode && (
        <button className="w-full py-4 bg-[#885ABB] text-white font-extrabold rounded-lg mt-2 mx-auto max-w-[98%] mb-2">
          SELECT A TIME TO REBOOK
        </button>
      )}
      <AppointmentSidebar
        isOpen={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setSelectedBooking(null);
        }}
        stylist={selectedSlot?.staff || ""}
        appointmentTime={selectedSlot?.time || ""}
        services={filteredServices}
        bookingData={selectedBooking}
        onRebook={() => setRebookMode(true)}
        staffList={staffList}
        setFilteredServices={setFilteredServices}
        servicesLoading={servicesLoading}
        appointmentDate={selectedSlot?.date || ""}
        mode="calendar"
        onRefresh={onRefresh}
      />
      {bookingLoading && (
        <GlobalLoader
          initialPageLoad={true}
          description="Loading booking details..."
          compensateSidebar={false}
        />
      )}
    </div>
  );
};

export default CalendarGrid;
